import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PlayerContentTitle from "core/components/PlayerContentTitle";

const PREFIX = "PlayerPause";

const classes = {
  root: `${PREFIX}-root`,
  inner: `${PREFIX}-inner`,
  close: `${PREFIX}-close`,
  visible: `${PREFIX}-visible`,
  transparent: `${PREFIX}-transparent`,
};

const Root = styled("div", {
  shouldForwardProp: (prop) => !["visible", "transparent"].includes(prop),
})(({ theme, visible, transparent }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1000,
  width: "100%",
  height: "100%",
  backgroundColor: transparent
    ? "transparent"
    : visible
    ? "rgba(0, 0, 0, 0.6)"
    : "rgba(0, 0, 0, 0)",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "stretch",
  padding: `${theme.spacing(5.75)} ${theme.spacing(2)}`,
  transition: "background-color .3s ease",

  ["@media (min-width: 375px)"]: {
    padding: `${theme.spacing(9)} ${theme.spacing(2.5)}`,
  },

  [theme.breakpoints.up("md")]: {
    padding: `${theme.spacing(9)} ${theme.spacing(3)}`,
  },

  // Маленький плеер: сверху место под шапку и крестик, снизу почти без
  // отступа, чтобы под кнопкой Play поместился ряд рекомендаций
  ["@media (max-width: 600px), (max-height: 375px)"]: {
    padding: `${theme.spacing(5.5)} ${theme.spacing(1.5)} ${theme.spacing(1)}`,
  },

  // Явный крестик: рекомендации на паузе возвращены в эмбед (они дают
  // глубину просмотра), но зритель должен легко их убрать
  [`& .${classes.close}`]: {
    position: "absolute",
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    zIndex: 1,
    width: 40,
    height: 40,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    opacity: visible ? 1 : 0,
    transition: "opacity .3s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
    },
  },

  [`& .${classes.inner}`]: {
    width: "100%",
    margin: "auto",
    opacity: visible ? 1 : 0,
    transition: "opacity .3s ease",

    maxWidth: 700,
    [theme.breakpoints.up("lg")]: {
      maxWidth: 800,
    },
  },
}));

const SHOW_TIMEOUT = 2000; // Кол-во миллисекунд, через которое покажется информация о контенте

export default function PlayerPause({
  title,
  contentUrl,
  relatedContent,
  onClickPlay,
  setVisible,
  visible,
  transparent,
  hideLogo,
  disableTitleLink,
  onClose,
}) {
  useEffect(() => {
    if (setVisible) {
      const show = () => {
        setVisible(true);
      };

      const timer = setTimeout(show, SHOW_TIMEOUT);

      return () => {
        clearInterval(timer);
      };
    }
  }, [setVisible]);

  return (
    <Root visible={visible} transparent={transparent} onClick={onClickPlay}>
      {onClose && (
        <IconButton
          className={classes.close}
          aria-label="Скрыть рекомендации"
          title="Скрыть рекомендации"
          onClick={(event) => {
            // Клик по фону блока продолжает видео, по крестику нет
            event.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <div className={classes.inner}>
        <PlayerContentTitle
          onClickPlay={onClickPlay}
          title={title}
          contentUrl={contentUrl}
          visible={visible}
          hideLogo={hideLogo}
          disableTitleLink={disableTitleLink}
        />

        {relatedContent}
      </div>
    </Root>
  );
}

PlayerPause.propTypes = {
  hideLogo: PropTypes.bool,
  disableTitleLink: PropTypes.bool,
  visible: PropTypes.bool,
  transparent: PropTypes.bool,
  title: PropTypes.string.isRequired,
  contentUrl: PropTypes.string.isRequired,
  relatedContent: PropTypes.node,
  onClickPlay: PropTypes.func,
  setVisible: PropTypes.func,
  onClose: PropTypes.func,
};
